import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { Home, Trophy, User, LogOut, Camera, Crown, Star, Shield, Menu, X, Image, Bell, Target, Users, Gift, Egg, Car, MapPin, Store, BarChart3 } from 'lucide-react';
import NotificationCenter from './NotificationCenter';
import { getAvatarUrl } from '../utils/avatarHelpers';

const Navigation = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const handleLogout = async () => {
    await logout();
    setMobileMenuOpen(false);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  if (!user) return null;

  // Debug: log user admin status

  return (
    <>
      {/* iOS Safe Area Background Fix */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-[env(safe-area-inset-top)] bg-slate-900 z-[60]" />
      
      {/* Premium Mobile Navigation - Optimized for Android */}
      <nav className="md:hidden fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-[#0f0c29] via-[#302b63] to-[#24243e] shadow-2xl" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo - Premium Design - Fixed for Android */}
            <Link to="/dashboard" className="flex items-center gap-2 hover:opacity-90 transition-all flex-shrink-0" style={{ minWidth: '44px' }}>
              <div className="relative" style={{ width: '40px', height: '40px', minWidth: '40px', minHeight: '40px' }}>
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full blur-md opacity-50"></div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_e5f13b3a-1688-45d4-b828-b05cd5eab0af/artifacts/dnynf6sv_fulllogo_transparent_nobuffer.png"
                  alt="CareWash"
                  className="relative z-10"
                  style={{ width: '40px', height: '40px', minWidth: '40px', minHeight: '40px', objectFit: 'contain' }}
                />
              </div>
            </Link>

            {/* Center: User Stats Pills */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-full px-3 py-1.5">
                <Crown size={14} className="text-yellow-400" />
                <span className="text-yellow-300 text-sm font-bold">{user.level}</span>
              </div>
              <div className="flex items-center gap-1.5 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-full px-3 py-1.5">
                <Star size={14} className="text-cyan-400" />
                <span className="text-cyan-300 text-sm font-bold">{user.total_points}</span>
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Notifications */}
              <NotificationCenter user={user} />
              
              {/* Profile Picture with glow */}
              <Link to="/profile" className="flex-shrink-0 relative">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full blur-sm opacity-60"></div>
                {getAvatarUrl(user) ? (
                  <img
                    src={getAvatarUrl(user)}
                    alt={user.name}
                    className="w-9 h-9 rounded-full object-cover border-2 border-white/30 bg-white relative z-10"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center border-2 border-white/30 relative z-10">
                    <span className="text-white font-bold text-sm">{user.name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}</span>
                  </div>
                )}
              </Link>
              
              {/* Menu Button - Glass effect */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-xl bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-all border border-white/10"
              >
                {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 bg-black/50 z-40 top-16" onClick={closeMobileMenu}>
            <div 
              className="absolute top-0 right-0 w-80 h-full bg-gradient-to-b from-slate-900 to-indigo-900 shadow-2xl overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* User Profile Section */}
              <div className="p-6 border-b border-slate-700">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-cyan-400 to-purple-500 rounded-full flex items-center justify-center">
                    <User className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">{user.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Crown size={14} className="text-yellow-400" />
                      <span className="text-cyan-300 text-sm">Niveau {user.level}</span>
                      <Star size={14} className="text-orange-400" />
                      <span className="text-orange-300 text-sm font-bold">{user.total_points} pts</span>
                    </div>
                    <p className="text-slate-400 text-sm mt-1">{user.level_name}</p>
                  </div>
                </div>
              </div>

              {/* Navigation Links */}
              <div className="p-4">
                <div className="space-y-2">
                  <Link 
                    to="/dashboard" 
                    onClick={closeMobileMenu}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isActive('/dashboard') || isActive('/') 
                        ? 'bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-white border border-cyan-500/30' 
                        : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                    }`}
                  >
                    <Home size={20} />
                    <div>
                      <div className="font-medium">Accueil</div>
                      <div className="text-xs opacity-75">Tableau de bord</div>
                    </div>
                  </Link>

                  <Link 
                    to="/leaderboard" 
                    onClick={closeMobileMenu}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isActive('/leaderboard') 
                        ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-white border border-yellow-500/30' 
                        : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                    }`}
                  >
                    <Trophy size={20} />
                    <div>
                      <div className="font-medium">Classement</div>
                      <div className="text-xs opacity-75">Top Champions</div>
                    </div>
                  </Link>

                  <Link 
                    to="/add-wash" 
                    onClick={closeMobileMenu}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isActive('/add-wash') 
                        ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-white border border-green-500/30' 
                        : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                    }`}
                  >
                    <Camera size={20} />
                    <div>
                      <div className="font-medium">Valider</div>
                      <div className="text-xs opacity-75">Photo + Points</div>
                    </div>
                  </Link>

                  <Link 
                    to="/reviews" 
                    onClick={closeMobileMenu}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isActive('/reviews') 
                        ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-white border border-blue-500/30' 
                        : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                    }`}
                  >
                    <Star size={20} />
                    <div>
                      <div className="font-medium">Avis CareWash</div>
                      <div className="text-xs opacity-75">+50 points</div>
                    </div>
                  </Link>

                  <Link 
                    to="/photos" 
                    onClick={closeMobileMenu}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isActive('/photos') 
                        ? 'bg-gradient-to-r from-pink-500/20 to-rose-500/20 text-white border border-pink-500/30' 
                        : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                    }`}
                  >
                    <Image size={20} />
                    <div>
                      <div className="font-medium">Photos</div>
                      <div className="text-xs opacity-75">Galerie communauté</div>
                    </div>
                  </Link>

                  <Link 
                    to="/challenges" 
                    onClick={closeMobileMenu}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isActive('/challenges') 
                        ? 'bg-gradient-to-r from-orange-500/20 to-red-500/20 text-white border border-orange-500/30' 
                        : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                    }`}
                  >
                    <Target size={20} />
                    <div>
                      <div className="font-medium">Défis</div>
                      <div className="text-xs opacity-75">Challenges</div>
                    </div>
                  </Link>

                  <Link 
                    to="/treasure" 
                    onClick={closeMobileMenu}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isActive('/treasure') 
                        ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-white border border-amber-500/30' 
                        : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                    }`}
                  >
                    <MapPin size={20} />
                    <div>
                      <div className="font-medium">Chasse au Trésor</div>
                      <div className="text-xs opacity-75">Trouve des coffres !</div>
                    </div>
                  </Link>

                  <Link 
                    to="/affluence" 
                    onClick={closeMobileMenu}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isActive('/affluence') 
                        ? 'bg-gradient-to-r from-purple-500/20 to-blue-500/20 text-white border border-purple-500/30' 
                        : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                    }`}
                  >
                    <BarChart3 size={20} />
                    <div>
                      <div className="font-medium">Affluence</div>
                      <div className="text-xs opacity-75">Heures creuses</div>
                    </div>
                  </Link>

                  <Link 
                    to="/mes-gains" 
                    onClick={closeMobileMenu}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isActive('/mes-gains') 
                        ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-white border border-amber-500/30' 
                        : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                    }`}
                  >
                    <Gift size={20} />
                    <div>
                      <div className="font-medium">Mes Gains</div>
                      <div className="text-xs opacity-75">Bons partenaires</div>
                    </div>
                  </Link>

                  <Link 
                    to="/creatures" 
                    onClick={closeMobileMenu}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isActive('/creatures') 
                        ? 'bg-gradient-to-r from-violet-500/20 to-purple-500/20 text-white border border-violet-500/30' 
                        : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                    }`}
                  >
                    <span className="text-xl">🐉</span>
                    <div>
                      <div className="font-medium">Mes Créatures</div>
                      <div className="text-xs opacity-75">Œufs & Collection</div>
                    </div>
                  </Link>

                  <Link 
                    to="/carmagotchi" 
                    onClick={closeMobileMenu}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isActive('/carmagotchi') 
                        ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-white border border-purple-500/30' 
                        : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                    }`}
                  >
                    <span className="text-xl">🥚</span>
                    <div>
                      <div className="font-medium">Carmagotchi</div>
                      <div className="text-xs opacity-75">Ma voiture virtuelle</div>
                    </div>
                  </Link>

                  <Link 
                    to="/friends" 
                    onClick={closeMobileMenu}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isActive('/friends') 
                        ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-white border border-cyan-500/30' 
                        : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                    }`}
                  >
                    <Users size={20} />
                    <div>
                      <div className="font-medium">Amis</div>
                      <div className="text-xs opacity-75">Mon réseau</div>
                    </div>
                  </Link>

                  <Link 
                    to="/referral" 
                    onClick={closeMobileMenu}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isActive('/referral') 
                        ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-white border border-yellow-500/30' 
                        : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                    }`}
                  >
                    <Gift size={20} />
                    <div>
                      <div className="font-medium">Parrainage</div>
                      <div className="text-xs opacity-75">Invite tes amis</div>
                    </div>
                  </Link>

                  <Link 
                    to="/mycar" 
                    onClick={closeMobileMenu}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isActive('/mycar') 
                        ? 'bg-gradient-to-r from-emerald-500/20 to-teal-500/20 text-white border border-emerald-500/30' 
                        : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                    }`}
                  >
                    <Car size={20} />
                    <div>
                      <div className="font-medium">Mon Garage</div>
                      <div className="text-xs opacity-75">Parking, carburant...</div>
                    </div>
                  </Link>

                  <Link 
                    to="/profile" 
                    onClick={closeMobileMenu}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isActive('/profile') 
                        ? 'bg-gradient-to-r from-indigo-500/20 to-purple-500/20 text-white border border-indigo-500/30' 
                        : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                    }`}
                  >
                    <User size={20} />
                    <div>
                      <div className="font-medium">Profil</div>
                      <div className="text-xs opacity-75">Paramètres</div>
                    </div>
                  </Link>

                  {/* Admin Link */}
                  {user.is_admin && (
                    <Link 
                      to="/admin" 
                      onClick={closeMobileMenu}
                      className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                        isActive('/admin') 
                          ? 'bg-gradient-to-r from-red-500/20 to-orange-500/20 text-white border border-red-500/30' 
                          : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                      }`}
                    >
                      <Shield size={20} />
                      <div>
                        <div className="font-medium">Admin</div>
                        <div className="text-xs opacity-75">Gestion</div>
                      </div>
                    </Link>
                  )}

                  {/* Partner Link */}
                  {(user.is_partner || user.is_admin) && (
                    <Link 
                      to="/partner" 
                      onClick={closeMobileMenu}
                      className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                        isActive('/partner') 
                          ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-white border border-amber-500/30' 
                          : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                      }`}
                    >
                      <Store size={20} />
                      <div>
                        <div className="font-medium">Mon Commerce</div>
                        <div className="text-xs opacity-75">Espace partenaire</div>
                      </div>
                    </Link>
                  )}
                </div>

                {/* App Info */}
                <div className="mt-8 p-4 bg-slate-800/30 rounded-xl border border-slate-700/50">
                  <p className="text-gray-400 text-xs mb-2">CareWash GO v3.0</p>
                  <p className="text-gray-500 text-xs">
                    L&apos;app de lavage la plus motivante !
                  </p>
                </div>

                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  className="w-full mt-4 flex items-center justify-center gap-2 p-3 bg-red-600/20 text-red-400 rounded-xl hover:bg-red-600/30 transition-all border border-red-600/30"
                >
                  <LogOut size={18} />
                  Se déconnecter
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Desktop Navigation with Safe Area */}
      <nav className="hidden md:flex fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-slate-900 via-purple-900 to-indigo-900 shadow-2xl border-b border-purple-500/20" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="w-full max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo - Clickable to go to Dashboard */}
            <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity flex-shrink-0 mr-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_e5f13b3a-1688-45d4-b828-b05cd5eab0af/artifacts/dnynf6sv_fulllogo_transparent_nobuffer.png"
                alt="CareWash Logo"
                style={{ width: '40px', height: '40px', minWidth: '40px', minHeight: '40px', objectFit: 'contain' }}
              />
              <div>
                <h1 className="text-white font-bold text-xl leading-none">CareWash</h1>
                <p className="text-cyan-300 text-sm leading-none">GO</p>
              </div>
            </Link>

            {/* Desktop Menu */}
            <div className="flex items-center gap-4 flex-wrap">
              <Link 
                to="/dashboard" 
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  isActive('/dashboard') || isActive('/') 
                    ? 'bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-white border border-cyan-500/30' 
                    : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                <Home size={18} />
                Accueil
              </Link>

              <Link 
                to="/leaderboard" 
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  isActive('/leaderboard') 
                    ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-white border border-yellow-500/30' 
                    : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                <Trophy size={18} />
                Classement
              </Link>

              <Link 
                to="/add-wash" 
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  isActive('/add-wash') 
                    ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-white border border-green-500/30' 
                    : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                <Camera size={18} />
                Valider
              </Link>

              <Link 
                to="/reviews" 
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  isActive('/reviews') 
                    ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-white border border-blue-500/30' 
                    : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                <Star size={18} />
                Avis
              </Link>

              <Link 
                to="/photos" 
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  isActive('/photos') 
                    ? 'bg-gradient-to-r from-pink-500/20 to-rose-500/20 text-white border border-pink-500/30' 
                    : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                <Image size={18} />
                Photos
              </Link>

              <Link 
                to="/challenges" 
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  isActive('/challenges') 
                    ? 'bg-gradient-to-r from-orange-500/20 to-red-500/20 text-white border border-orange-500/30' 
                    : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                <Target size={18} />
                Défis
              </Link>

              <Link 
                to="/carmagotchi" 
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  isActive('/carmagotchi') 
                    ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-white border border-purple-500/30' 
                    : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                <span>🥚</span>
                Carmagotchi
              </Link>

              <Link 
                to="/friends" 
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  isActive('/friends') 
                    ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-white border border-cyan-500/30' 
                    : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                <Users size={18} />
                Amis
              </Link>

              <Link 
                to="/referral" 
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  isActive('/referral') 
                    ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-white border border-yellow-500/30' 
                    : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                <Gift size={18} />
                Parrainage
              </Link>

              <Link 
                to="/mycar" 
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  isActive('/mycar') 
                    ? 'bg-gradient-to-r from-emerald-500/20 to-teal-500/20 text-white border border-emerald-500/30' 
                    : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                <Car size={18} />
                Mon Garage
              </Link>

              {/* Admin Link (Desktop) */}
              {user.is_admin && (
                <Link 
                  to="/admin" 
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    isActive('/admin') 
                      ? 'bg-gradient-to-r from-red-500/20 to-orange-500/20 text-white border border-red-500/30' 
                      : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                  }`}
                >
                  <Shield size={18} />
                  Admin
                </Link>
              )}

              {/* User Section */}
              <div className="flex items-center gap-4 ml-4">
                <NotificationCenter user={user} />
                <div className="text-right">
                  <p className="text-white font-bold text-sm">{user.name}</p>
                  <div className="flex items-center gap-2 justify-end">
                    <Crown size={12} className="text-yellow-400" />
                    <span className="text-cyan-300 text-xs">Niv. {user.level}</span>
                    <Star size={12} className="text-orange-400" />
                    <span className="text-orange-300 text-xs font-bold">{user.total_points}pts</span>
                  </div>
                </div>

                <Link 
                  to="/profile"
                  className="hover:scale-105 transition-transform"
                >
                  {getAvatarUrl(user) ? (
                    <img
                      src={getAvatarUrl(user)}
                      alt={user.name}
                      className="w-10 h-10 rounded-full object-cover border-2 border-cyan-400 bg-white"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-purple-500 rounded-full flex items-center justify-center">
                      <User className="text-white" size={18} />
                    </div>
                  )}
                </Link>

                {/* Logout Button (Desktop) */}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-3 py-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 transition-all border border-red-600/30"
                  title="Se déconnecter"
                >
                  <LogOut size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Spacer to prevent content from being hidden behind fixed nav */}
      <div className="h-16 md:h-20"></div>
    </>
  );
};

export default Navigation;